import type { Vehicle, AxleConfigEntry } from '../types/optimization'
import { optionalVehicleNumber } from './vehicleFormContract'

export type AxleDiagramKind = 'semi' | 'trailer' | 'rigid' | 'tractor' | 'van' | 'custom'

export function axleDiagramKind(vehicle: Vehicle): AxleDiagramKind {
  const config = typeof vehicle.axle_configuration === 'object' ? vehicle.axle_configuration : null
  const physics = config?.physicsType
  if (vehicle.type === 'SEMI_TRAILER') return 'semi'
  if (vehicle.type === 'TRAILER') return 'trailer'
  if (vehicle.type === 'VAN') return 'van'
  if (vehicle.type === 'RIGID_TRUCK') return 'rigid'
  if (('isTractor' in vehicle && vehicle.isTractor === true) || vehicle.vehicle_subtype?.startsWith('tracteur_') || physics === 'truck' || (vehicle.type === 'TRUCK' && (
    optionalVehicleNumber(vehicle.max_combination_weight) != null || config?.fifthWheelPosition != null || config?.fifthWheelMaxLoad != null
  ))) return 'tractor'
  if (vehicle.type === 'TRUCK') return 'rigid'
  if (physics === 'tractor_semitrailer') return 'semi'
  if (physics === 'trailer') return 'trailer'
  if (physics === 'van') return 'van'
  if (physics === 'rigid_truck') return 'rigid'
  return 'custom'
}

/** Number by longitudinal position, retaining the source index and persistent ID. */
export function numberedAxles(axles: AxleConfigEntry[]) {
  return axles.map((axle, index) => ({ axle, index }))
    .sort((a, b) => (optionalVehicleNumber(a.axle.position) ?? 0) - (optionalVehicleNumber(b.axle.position) ?? 0) || a.index - b.index)
    .map((entry, index) => ({ ...entry, number: index + 1 }))
}

export function axleMembersLabel(ids: string[], axles: AxleConfigEntry[]) {
  const members = numberedAxles(axles).filter(entry => ids.includes(entry.axle.id)).map(entry => entry.number)
  return members.length ? `${members.length === 1 ? 'Essieu' : 'Essieux'} ${members.join(', ')}` : 'Aucun essieu sélectionné'
}

/** Canonical coupling values, preserving unknown values and explicit zeroes. */
export function vehicleCoupling(vehicle: Vehicle | null | undefined, isTractor = false) {
  if (!vehicle) return null
  const kind = isTractor ? 'tractor' : axleDiagramKind(vehicle)
  if (kind !== 'semi' && kind !== 'tractor') return null
  const config = typeof vehicle.axle_configuration === 'object' ? vehicle.axle_configuration : null
  const positionField = kind === 'tractor' ? 'fifthWheelPosition' : 'kingPinPosition'
  const persisted = (field: string, legacy: unknown) => optionalVehicleNumber(
    config && Object.prototype.hasOwnProperty.call(config, field) ? (config as any)[field] : legacy
  )
  return {
    positionField,
    tractor: kind === 'tractor',
    position: persisted(positionField, vehicle.fifth_wheel?.position),
    maxLoad: persisted('fifthWheelMaxLoad', vehicle.fifth_wheel?.maxLoad),
  }
}

export function updateVehicleCoupling(vehicle: Vehicle, field: 'position' | 'maxLoad', value: number | null, isTractor = false): Vehicle {
  const coupling = vehicleCoupling(vehicle, isTractor)
  if (!coupling) return vehicle
  const config = typeof vehicle.axle_configuration === 'object' ? vehicle.axle_configuration : null
  return {
    ...vehicle,
    axle_configuration: { axles: [], ...config, [field === 'position' ? coupling.positionField : 'fifthWheelMaxLoad']: value },
    fifth_wheel: { ...vehicle.fifth_wheel, position: field === 'position' ? value : coupling.position, maxLoad: field === 'maxLoad' ? value : coupling.maxLoad },
  } as Vehicle
}
